// -- [Lightweight Markdown-like rendering: **bold**, <strong>, \n\n = paragraphs, \n = line break. No extra deps.]

import type { ReactNode } from 'react'

/** -- Split inline text into plain segments and <strong> / **bold** spans */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(/(<strong>[\s\S]*?<\/strong>|\*\*[^*]+\*\*)/gi)
  return parts.flatMap((seg, j) => {
    if (!seg) return []
    const strongHtml = seg.match(/^<strong>([\s\S]*?)<\/strong>$/i)
    if (strongHtml) {
      return [<strong key={`${keyPrefix}-s-${j}`}>{strongHtml[1]}</strong>]
    }
    const boldMd = seg.match(/^\*\*(.+)\*\*$/)
    if (boldMd) {
      return [<strong key={`${keyPrefix}-b-${j}`}>{boldMd[1]}</strong>]
    }
    return seg.split('\n').flatMap((line, k) =>
      k === 0 ? [line] : [<br key={`${keyPrefix}-br-${j}-${k}`} />, line]
    )
  })
}

/**
 * Renders a string with simple Markdown-style formatting:
 * - **text** or <strong>text</strong> → bold
 * - \n\n → new paragraph (block)
 * - \n → line break
 */
export function MarkdownLike({
  text,
  className,
  asBlock = true,
}: {
  text: string
  className?: string
  /** When true (default), \n\n creates <p> blocks; when false, only ** / <strong> and \n are applied inline */
  asBlock?: boolean
}): ReactNode {
  if (!text) return null

  const paragraphs = asBlock ? text.split(/\n\n+/) : [text.replace(/\n\n+/g, '\n')]
  const Wrapper = asBlock ? 'div' : 'span'

  return (
    <Wrapper className={className}>
      {paragraphs.map((para, i) => {
        const content = renderInline(para, `${i}`)
        if (asBlock && paragraphs.length > 1) {
          return (
            <p key={i} className={i > 0 ? 'mt-2' : undefined}>
              {content}
            </p>
          )
        }
        return <span key={i}>{content}</span>
      })}
    </Wrapper>
  )
}
